import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { ReactComponent as EmptyState } from '@bechtel/client-assets/dist/images/empty-state-weld-dashboard.svg';
import {
  ErrorBoundary,
  StatsSpinner,
  SuspenseLoader,
  useAppUser,
  useModulePermission,
  useTwxEnabled,
  useWeldAssignees,
} from '@bechtel/client-common';
import { GET_DASHBOARD_WELDS } from '@bechtel/client-gql';
import { NewRodRequest, useRodRoomPermissions, useWeldPermissions } from '@bechtel/client-weld-common';
import { Box, Button, ButtonSize, Icon, Spinner, SpinnerSize, Text, useSideSheetState } from '@bechtel/gds';
import {
  CommodityAssignee,
  CommodityComplexFilterInput,
  CommodityFilterByTerm,
  CommodityFilterInput,
  CommodityFilterStatusFlag,
  CommodityList,
  CommoditySortByTerm,
  CommodityStats,
  CommodityStatusCode,
  FilterOperator,
  PagingInput,
  PersonaType,
  SortOrder,
  Weld,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { filter, find, forEach, get, includes, isEqual, map, some } from 'lodash';
import { FC, Suspense, useEffect, useRef, useState } from 'react';
import { useCommodityDashboardTabStatsQuery, useCommodityFitupStatsQuery, useWeeklyCommodityStatsQuery } from '~/hooks';
import { BulkRequest } from './bulk-request';
import { ListStyled, TabButtonStyled, WeldListTabsContainer, WeldListTabStyled } from './styled';
import { WeldListItemTab, WeldTabProps } from './types';
import { WeldItem } from './weld-item';
const pageLimit = 10;
export function usePreviousWeldFilter(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}
export const WeldTabs: FC<WeldTabProps> = ({
  personaType,
  weldFilter,
  crewMember,
  weldTypeId,
  fitupStatusStats,
  weeklyStatusStats,
  loading,
}) => {
  const containerRef = useRef(null);
  const { user } = useAppUser();
  const { canViewRodRoomModule } = useModulePermission();
  const { twxEnabled } = useTwxEnabled();
  const [selectedWelds, setSelectedWelds] = useState<Weld[]>();
  const [selectedTab, setSelectedTab] = useState<CommodityStatusCode>(CommodityStatusCode.READY_TO_WELD);
  const [bulkRequest, setBulkRequest] = useState<boolean>(false);
  const [selectedWeldItems, setSelectedWeldItems] = useState<string[]>([]);
  const [allowItemSelection, setAllowItemSelection] = useState<boolean>(false);
  const [
    loadCommodityStats,
    { loading: weldTabStatsLoading, data: weldTabStatsData },
  ] = useCommodityDashboardTabStatsQuery(true);
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  const { i18n } = useLingui();

  const weldTabsList: WeldListItemTab[] = [
    { label: t`Fitup Inspection`, value: CommodityStatusCode.FITUP_INSPECTION, visible: !twxEnabled },
    { label: t`Ready to Weld`, value: CommodityStatusCode.READY_TO_WELD, visible: true },
    { label: t`Claimed`, value: CommodityStatusCode.CLAIMED, visible: true },
    {
      label: t`VT Approved This Week`,
      value: CommodityStatusCode.VT_APPROVED,
      visible: personaType === PersonaType.FIELD_WELD_ENGINEER,
    },
  ];

  const {
    visible: visibleNewRodRequest,
    open: openNewRodRequestSideSheet,
    close: closeNewRodRequestSideSheet,
  } = useSideSheetState(false);
  const [
    getWeeklyCommodityStats,
    { data: weeklyCommodityStatsData, loading: weeklyCommodityStatsLoading, called: weeklyCommodityStatsCalled },
  ] = useWeeklyCommodityStatsQuery(true);
  const [
    getCommodityFitupStats,
    { data: fitupStatsData, loading: fitupLoading, called: fitupCalled },
  ] = useCommodityFitupStatsQuery(true);
  const createRodRequest = (weld) => {
    setSelectedWelds([weld]);
    openNewRodRequestSideSheet();
  };

  const createBulkRodRequest = () => {
    setSelectedWelds(
      weldList.filter((weld) => find(selectedWeldItems, (selectedWeldId) => selectedWeldId === weld.id))
    );
    openNewRodRequestSideSheet();
  };

  const filterWeldList = (selectedTab: any) => {
    setSelectedTab(selectedTab);
    setBulkRequest(false);
    setSelectedWeldItems([]);
    setAllowItemSelection(false);
    containerRef.current = null;
  };

  const selectWeldItem = (id: string) => {
    const selectedItem = find(selectedWeldItems, (item) => item === id);

    if (selectedItem) {
      setSelectedWeldItems(
        filter(selectedWeldItems, (item) => {
          return item !== id;
        })
      );
    } else {
      setSelectedWeldItems([...selectedWeldItems, id]);
    }
  };

  const selectAllWeldList = (isWPS?: boolean) => {
    if (selectedWeldItems.length === weldList.length || isWPS) {
      setSelectedWeldItems([]);
    } else {
      const selectedItems: string[] = [];
      map(weldList, (item) => selectedItems.push(item.id));
      setSelectedWeldItems(selectedItems);
    }
  };

  const allowSelection = (value: boolean) => {
    setAllowItemSelection(value);
    if (!value) {
      setSelectedWeldItems([]);
    }
  };

  const [getWelds, { loading: weldListLoading, data, error, refetch, networkStatus, called, fetchMore }] = useLazyQuery<
    { commodities: CommodityList },
    {
      input: CommodityFilterInput;
      paging: PagingInput;
    }
  >(GET_DASHBOARD_WELDS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const getWeldFilters = () => {
    const complexFilterInput: CommodityComplexFilterInput = {
      and: [
        {
          term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
          value: weldTypeId,
          operator: FilterOperator.EQUALS,
        },
      ],
      or: [],
    };
    if (personaType === PersonaType.FIELD_WELD_ENGINEER) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.APPLY_ASSOCIATED_CREW_MEMBERS,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });

      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.PRIMARY_CREW_MEMBER_ID,
        values: weldFilter?.searchByFWE?.length > 0 ? [crewMember?.id, ...weldFilter.searchByFWE] : [crewMember?.id],
        operator: FilterOperator.IN,
      });
    } else {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.PRIMARY_CREW_MEMBER_ID,
        value: crewMember?.id,
        operator: FilterOperator.EQUALS,
      });
    }
    if (crewMember?.persona && crewMember.persona === PersonaType.FIELD_WELD_ENGINEER) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.PERSONA_TYPE_ID,
        value: crewMember?.persona,
        operator: FilterOperator.EQUALS,
      });
    }

    if (weldFilter?.searchByWPS?.length > 0) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.WELD_PROCEDURE_ID,
        values: weldFilter?.searchByWPS,
        operator: FilterOperator.IN,
      });
    }

    if (weldFilter?.searchByForeman?.length > 0) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.CREW_MEMBER_ID,
        values: [...weldFilter.searchByForeman],
        operator: FilterOperator.IN,
      });
    }

    if (weldFilter?.searchByDrawing?.length > 0) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.DRAWING_NUMBER,
        values: weldFilter?.searchByDrawing,
        operator: FilterOperator.IN,
      });
    }
    if (weldFilter?.drawerValues?.weldTypes?.length > 0)
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.CONNECTION_TYPE,
        values: weldFilter?.drawerValues?.weldTypes,
        operator: FilterOperator.IN,
      });
    if (weldFilter?.drawerValues?.weldSizeList?.length === 1) {
      const isSmallBore = some(weldFilter?.drawerValues?.weldSizeList, (item) => {
        return item === CommodityFilterByTerm.SMALL_BORE;
      });
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.NOMINAL_DIAMETER,
        value: '2.5',
        operator: isSmallBore ? FilterOperator.LESS_THAN : FilterOperator.GREATER_THAN,
      });
    }
    if (weldFilter?.drawerValues?.isPriority) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.PRIORITY_WELDS,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
    }
    if (weldFilter?.searchKeyword.length > 0)
      forEach(weldFilter?.drawerValues?.searchByList, (item: CommodityFilterByTerm) => {
        complexFilterInput.or?.push({
          term: item,
          value: weldFilter?.searchKeyword,
          operator: item == CommodityFilterByTerm.CREW_MEMBER_NAME ? FilterOperator.FUZZY : FilterOperator.CONTAINS,
        });
      });

    return complexFilterInput;
  };

  useEffect(() => {
    if (
      crewMember?.persona === PersonaType.SUPER_ADMIN ||
      (crewMember?.persona === PersonaType.FIELD_WELD_ENGINEER && !weldFilter?.searchByForeman?.length)
    )
      return;
    if (!weldTypeId) return;
    if (weldFilter?.searchKeyword?.length > 0 && weldFilter?.searchKeyword?.length < 3) return;

    const commodityComplexFilterInput = getWeldFilters();

    const selectedTabStatus =
      selectedTab === CommodityStatusCode.READY_TO_WELD
        ? [CommodityStatusCode.READY_TO_WELD, CommodityStatusCode.VT_SENT_BACK]
        : selectedTab === CommodityStatusCode.CLAIMED
        ? [CommodityStatusCode.CLAIMED]
        : selectedTab === CommodityStatusCode.FITUP_INSPECTION
        ? [CommodityStatusCode.FITUP_INSPECTION]
        : [CommodityStatusCode.VT_APPROVED];

    if (selectedTab === CommodityStatusCode.VT_APPROVED) {
      commodityComplexFilterInput.and?.push(
        {
          term: CommodityFilterByTerm.START_DATE,
          value: currentProjectWeek?.startDate,
          operator: FilterOperator.GREATER_THAN,
        },
        {
          term: CommodityFilterByTerm.END_DATE,
          value: currentProjectWeek?.endDate,
          operator: FilterOperator.LESS_THAN,
        }
      );
    }
    if (!includes([CommodityStatusCode.CLAIMED, CommodityStatusCode.VT_APPROVED], selectedTab)) {
      commodityComplexFilterInput.and?.push({
        term: CommodityFilterByTerm.INCLUDE_BLOCKED_COMMODITY,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
    }

    if (!(personaType === PersonaType.FIELD_WELD_ENGINEER && selectedTab === CommodityStatusCode.FITUP_INSPECTION))
      commodityComplexFilterInput.and?.push({
        term: CommodityFilterByTerm.ASSIGNED_CREW_MEMBER,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
    commodityComplexFilterInput.and?.push({
      term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
      values:
        weldFilter?.drawerValues?.weldStatusList?.length > 0
          ? [...weldFilter.drawerValues.weldStatusList, ...selectedTabStatus].filter(
              (value, index, self) => self.indexOf(value) === index
            )
          : [...selectedTabStatus],

      operator: FilterOperator.IN,
    });
    getWelds({
      variables: {
        input: {
          complexFilter: commodityComplexFilterInput,
          sortByFilter: {
            term: weldFilter?.drawerValues?.sortBy ? weldFilter?.drawerValues?.sortBy : CommoditySortByTerm.TAG,
            sortOrder: weldFilter?.drawerValues?.sortDir ? weldFilter?.drawerValues?.sortDir : SortOrder.DESC,
          },
        },
        paging: {
          cursor: '1',
          limit: pageLimit,
        },
      },
    });
  }, [selectedTab, weldFilter, weldTypeId, crewMember, getWelds, personaType]);

  const prevWeldFilter = usePreviousWeldFilter(weldFilter);
  useEffect(() => {
    if (
      crewMember?.persona === PersonaType.SUPER_ADMIN ||
      (crewMember?.persona === PersonaType.FIELD_WELD_ENGINEER && !weldFilter?.searchByForeman?.length)
    )
      return;
    if (!weldTypeId) return;
    if (weldFilter?.searchKeyword?.length > 0 && weldFilter?.searchKeyword?.length < 3) return;

    const commodityComplexFilterInput = getWeldFilters();
    commodityComplexFilterInput.and?.push({
      term: CommodityFilterByTerm.INCLUDE_BLOCKED_COMMODITY,
      value: 'true',
      operator: FilterOperator.EQUALS,
    });
    if (!(personaType === PersonaType.FIELD_WELD_ENGINEER && selectedTab === CommodityStatusCode.FITUP_INSPECTION))
      commodityComplexFilterInput.and?.push({
        term: CommodityFilterByTerm.ASSIGNED_CREW_MEMBER,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
    if (personaType === PersonaType.FIELD_WELD_ENGINEER) {
      commodityComplexFilterInput.and?.push({
        term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
        values: [CommodityStatusCode.CLAIMED, CommodityStatusCode.READY_TO_WELD, CommodityStatusCode.VT_SENT_BACK],
        operator: FilterOperator.IN,
      });
    } else {
      commodityComplexFilterInput.and?.push({
        term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
        values: [
          CommodityStatusCode.READY_TO_WELD,
          CommodityStatusCode.FITUP_INSPECTION,
          CommodityStatusCode.VT_SENT_BACK,
          CommodityStatusCode.CLAIMED,
        ],
        operator: FilterOperator.IN,
      });
    }

    loadCommodityStats({
      variables: {
        input: {
          complexFilter: {
            ...commodityComplexFilterInput,
          },
        },
      },
    });

    if (prevWeldFilter && !isEqual(weldFilter, prevWeldFilter) && personaType === PersonaType.FIELD_WELD_ENGINEER) {
      const weeklyComplexFilterInput = getWeldFilters();
      weeklyComplexFilterInput.and?.push(
        {
          term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
          value: CommodityStatusCode.VT_APPROVED,
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.ASSIGNED_CREW_MEMBER,
          value: 'true',
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.START_DATE,
          value: currentProjectWeek?.startDate,
          operator: FilterOperator.GREATER_THAN,
        },
        {
          term: CommodityFilterByTerm.END_DATE,
          value: currentProjectWeek?.endDate,
          operator: FilterOperator.LESS_THAN,
        }
      );
      getWeeklyCommodityStats({
        variables: {
          input: {
            complexFilter: {
              ...weeklyComplexFilterInput,
            },
          },
        },
      });

      const fitupComplexFilterInput = getWeldFilters();
      fitupComplexFilterInput.and?.push(
        {
          term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
          value: CommodityStatusCode.FITUP_INSPECTION,
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.INCLUDE_BLOCKED_COMMODITY,
          value: 'true',
          operator: FilterOperator.EQUALS,
        }
      );

      if (!twxEnabled) {
        getCommodityFitupStats({
          variables: {
            input: {
              complexFilter: fitupComplexFilterInput,
            },
          },
        });
      }
    }
  }, [weldFilter, weldTypeId, crewMember, loadCommodityStats]);

  const statusStats = get(weldTabStatsData?.commodityStats, 'commodityStats.statusStats');

  const getStats = (statusFlag: CommodityFilterStatusFlag) => {
    return (
      get(
        find(statusStats, (statusStat) => statusStat?.status === statusFlag),
        'stats'
      ) || 0
    );
  };
  const filteredWeeklyStatusStats: CommodityStats[] = get(
    weeklyCommodityStatsData?.commodityStats,
    'commodityStats.statusStats',
    []
  );

  const getWeeklyStats = (statusFlag: CommodityFilterStatusFlag) => {
    return (
      get(
        find(
          weeklyCommodityStatsCalled ? filteredWeeklyStatusStats : weeklyStatusStats,
          (statusStat) => statusStat?.status === statusFlag
        ),
        'stats'
      ) || 0
    );
  };
  const filteredFitupStatusStats = get(fitupStatsData?.commodityStats, 'commodityStats.statusStats');
  const getFitupStats = (statusFlag: CommodityFilterStatusFlag) => {
    return (
      get(
        find(
          fitupCalled ? filteredFitupStatusStats : fitupStatusStats,
          (statusStat) => statusStat?.status === statusFlag
        ),
        'stats'
      ) || 0
    );
  };
  const numFitupInspectionFWE = getFitupStats(CommodityFilterStatusFlag.FITUP_INSPECTION);
  const numClaimed = getStats(CommodityFilterStatusFlag.CLAIMED);
  const numFitupInspection = getStats(CommodityFilterStatusFlag.FITUP_INSPECTION);

  const numReadyToWeld = getStats(CommodityFilterStatusFlag.READY_TO_WELD);
  const numApproved = getWeeklyStats(CommodityFilterStatusFlag.VT_APPROVED);
  const numVTSentBack = getStats(CommodityFilterStatusFlag.VT_SENT_BACK);
  // NOTE: allows us to safely access "data.commodities.items" during all lifecycles
  const weldList: Weld[] = map(get(data, 'commodities.items', []), 'weld');
  const { commodityAssignees = null } = weldList && weldList?.length > 0 && weldList[0] ? weldList[0] : {};
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(commodityAssignees);
  const { canAssignFieldWeldEngineer } = useWeldPermissions(assignedCrewMembers);
  const { canCreateRodRequest } = useRodRoomPermissions(assignedCrewMembers);
  const isBulkAssign: boolean =
    (canAssignFieldWeldEngineer && selectedTab === CommodityStatusCode.CLAIMED) ||
    (canViewRodRoomModule && canCreateRodRequest && selectedTab === CommodityStatusCode.READY_TO_WELD);
  const totalSelectedWelds =
    selectedTab === CommodityStatusCode.READY_TO_WELD
      ? numReadyToWeld + numVTSentBack
      : selectedTab === CommodityStatusCode.FITUP_INSPECTION
      ? crewMember?.persona === PersonaType.FIELD_WELD_ENGINEER
        ? numFitupInspectionFWE
        : numFitupInspection
      : selectedTab === CommodityStatusCode.CLAIMED
      ? numClaimed
      : numApproved;
  const totalPages = totalSelectedWelds ? Math.ceil(totalSelectedWelds / pageLimit) : 0;
  const weldListCursor = get(data, 'commodities.cursor', 1);
  useEffect(() => {
    const container = containerRef.current;
    //if list items fit in screen it means scrollHeight and clientHeight are same than scroll bar will not come and we need make one more hit because totalpages>1 and pagelimit record already renderded
    if (
      container &&
      container?.scrollHeight == container?.clientHeight &&
      weldList?.length >= pageLimit &&
      totalPages > 1
    ) {
      fetchMore({
        variables: {
          paging: {
            cursor: weldListCursor.toString(),
            limit: pageLimit,
          },
        },
      });
    }
  }, [weldList]);

  const handleScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      weldListCursor <= totalPages &&
      !weldListLoading
    ) {
      fetchMore({
        variables: {
          paging: {
            cursor: weldListCursor.toString(),
            limit: pageLimit,
          },
        },
      });
    }
  };

  return (
    <WeldListTabsContainer data-testid="weld-tabs-container">
      <Box
        alignItems={{ sm: 'center' }}
        justifyContent={{ sm: 'space-between' }}
        data-testid="weld-list"
        css={{ borderBottom: `1px solid grey` }}
      >
        <WeldListTabStyled>
          {map(weldTabsList, (tabItem: WeldListItemTab) => {
            return (
              tabItem.visible && (
                <TabButtonStyled
                  data-testid="weld-tab-button"
                  selected={tabItem.value === selectedTab}
                  onClick={() => {
                    filterWeldList(tabItem.value);
                  }}
                  key={tabItem.value}
                >
                  {i18n._(tabItem.label)} (
                  {weldTabStatsLoading || loading || weeklyCommodityStatsLoading || fitupLoading ? (
                    <StatsSpinner></StatsSpinner>
                  ) : tabItem.value === CommodityStatusCode.READY_TO_WELD ? (
                    numReadyToWeld + numVTSentBack
                  ) : tabItem.value === CommodityStatusCode.FITUP_INSPECTION ? (
                    numFitupInspectionFWE ? (
                      numFitupInspectionFWE
                    ) : (
                      numFitupInspection
                    )
                  ) : tabItem.value === CommodityStatusCode.CLAIMED ? (
                    numClaimed
                  ) : (
                    numApproved
                  )}
                  )
                </TabButtonStyled>
              )
            );
          })}
        </WeldListTabStyled>
      </Box>
      {isBulkAssign && crewMember?.persona !== PersonaType.SUPER_ADMIN && (
        <BulkRequest
          selectAllWeldList={selectAllWeldList}
          selectedWeldIds={selectedWeldItems}
          bulkRequest={bulkRequest}
          setBulkRequest={(value) => setBulkRequest(value)}
          weldListCount={weldList?.length}
          selectItems={allowSelection}
          createBulkRodRequest={createBulkRodRequest}
          selectedTab={selectedTab}
          setSelectedWeldItems={setSelectedWeldItems}
          canAssignFieldWeldEngineer={canAssignFieldWeldEngineer}
          canCreateRodRequest={canViewRodRoomModule && canCreateRodRequest}
        />
      )}
      {weldList && weldList?.length > 0 && (
        <ListStyled
          onScroll={(e) => {
            handleScroll(e);
          }}
          ref={containerRef}
        >
          {map(weldList, (weld: Weld) => (
            <ErrorBoundary key={weld?.id}>
              <WeldItem
                weld={weld}
                showSelectOption={bulkRequest}
                onSelectWeldItem={selectWeldItem}
                isChecked={selectedWeldItems.indexOf(weld?.id) !== -1}
                allowItemSelection={allowItemSelection}
                personaType={personaType}
                createRodRequest={createRodRequest}
              />
            </ErrorBoundary>
          ))}
        </ListStyled>
      )}
      {(networkStatus === NetworkStatus.refetch || weldListLoading) && (
        <Box
          p={{ sm: '500' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          css={weldList.length === 0 ? 'height: 100%;' : ''}
        >
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!weldListLoading && error && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          p={{ sm: '500' }}
        >
          <Box pb={{ sm: '500' }}>
            <Text>
              <Trans>There was a problem loading Welds. Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Button onClick={() => refetch()} size={ButtonSize.SMALL} css={{ width: '10%' }}>
            <Box mr={{ sm: '400' }}>
              <Icon icon="undo" />
            </Box>
            <Trans>Retry</Trans>
          </Button>
        </Box>
      )}
      {called && !weldListLoading && !error && weldList.length === 0 && (
        <Box
          justifyContent={{ sm: 'center' }}
          alignItems={{ sm: 'center' }}
          flexDirection={{ sm: 'column' }}
          gap={{ sm: '550' }}
          pt={{ sm: '550' }}
          style={{ flex: 1 }}
        >
          <EmptyState />
          <Text color="grey.1000" weight="600" family="graphik">
            <Trans>No results found</Trans>
          </Text>
          <Text color="grey.1000" family="graphik">
            <Trans>Try changing your search criteria or editing your filters.</Trans>
          </Text>
        </Box>
      )}
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visibleNewRodRequest && selectedWelds?.length && (
            <NewRodRequest onClose={closeNewRodRequestSideSheet} welds={selectedWelds} />
          )}
        </Suspense>
      </ErrorBoundary>
    </WeldListTabsContainer>
  );
};
